import { LabelInterface, ColorItem } from "@/types/labels";
import { toast } from "sonner";

const MAX_RETRIES = 3;
const BASE_DELAY = 1000;

/**
 * Retry function with exponential backoff to fix race condition
 */
export const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = MAX_RETRIES,
): Promise<T> => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      // Wait with exponential backoff before retrying
      if (i > 0) {
        await new Promise((resolve) =>
          setTimeout(resolve, Math.pow(2, i - 1) * BASE_DELAY),
        );
      }
      return await fn();
    } catch (error) {
      if (i === maxRetries - 1) {
        throw error;
      }
      console.warn(`Retry ${i + 1}/${maxRetries} failed:`, error);
    }
  }
  throw new Error(`Failed after ${maxRetries} retries`);
};

/**
 * Shared label creation logic for bulk updates
 */
export const createLabelWithRetry = async (
  newLabel: LabelInterface,
  projectId: string,
  supabaseClient: any,
  updateIssueLabels: (
    supabaseClient: any,
    issueIdToLabels: { issueId: string; labels: LabelInterface[] }[],
  ) => void | Promise<void>,
  currentIssueId: string,
  currentLabels: string[],
  projectLabels: LabelInterface[],
): Promise<void> => {
  try {
    await retryWithBackoff(async () => {
      const { data, error } = await supabaseClient
        .from("projects")
        .select("*")
        .eq("id", projectId)
        .single();

      if (error) {
        throw error;
      }

      const labels = data?.labels as any[];
      const label = labels?.find((l: any) => l.id === newLabel.id);

      if (!label) {
        throw new Error("Label not found after creation");
      }

      const currentLabelInterfaces = currentLabels
        .map((l) => projectLabels.find((pl) => pl.id === l))
        .filter(Boolean) as LabelInterface[];

      await updateIssueLabels(supabaseClient, [
        {
          issueId: currentIssueId,
          labels: [...currentLabelInterfaces, label],
        },
      ]);

      return data;
    });

    toast.success(`Created new label: ${newLabel.name}`);
  } catch (error) {
    console.error("Failed to create label:", error);
    toast.error("Failed to create label. Please try again.");
    throw error;
  }
};

/**
 * Shared label creation logic for single issue updates
 */
export const createLabelWithRetrySingle = async (
  newLabel: LabelInterface,
  projectId: string,
  supabaseClient: any,
  updateIssueLabels: (
    supabaseClient: any,
    issueId: string,
    labels: LabelInterface[],
  ) => void | Promise<void>,
  currentIssueId: string,
  currentLabels: string[],
  projectLabels: LabelInterface[],
): Promise<void> => {
  try {
    await retryWithBackoff(async () => {
      const { data, error } = await supabaseClient
        .from("projects")
        .select("*")
        .eq("id", projectId)
        .single();

      if (error) {
        throw error;
      }

      const labels = data?.labels as any[];
      const label = labels?.find((l: any) => l.id === newLabel.id);

      if (!label) {
        throw new Error("Label not found after creation");
      }

      const currentLabelInterfaces = currentLabels
        .map((l) => projectLabels.find((pl) => pl.id === l))
        .filter(Boolean) as LabelInterface[];

      await updateIssueLabels(supabaseClient, currentIssueId, [
        ...currentLabelInterfaces,
        label,
      ]);

      return data;
    });

    toast.success(`Created new label: ${newLabel.name}`);
  } catch (error) {
    console.error("Failed to create label:", error);
    toast.error("Failed to create label. Please try again.");
    throw error;
  }
};

/**
 * Sanitize label name to prevent XSS and ensure valid input
 */
export const sanitizeLabelName = (name: string): string => {
  return name.trim().replace(/[<>]/g, "").substring(0, 50);
};

/**
 * Validate label name
 */
export const validateLabelName = (name: string): boolean => {
  const sanitized = sanitizeLabelName(name);
  return sanitized.length > 0 && sanitized.length <= 50;
};

/**
 * Safely extracts color value from label color property
 * Handles all possible color formats including nested structures
 * @param color - The color property from a label
 * @returns A string color value or fallback color
 */
export const getColorValue = (color: any): string => {
  if (typeof color === "string") {
    return color;
  }

  if (color && typeof color === "object") {
    // Handle nested value object: { key: "...", label: "...", value: { value: "#..." } }
    if (
      color.value &&
      typeof color.value === "object" &&
      "value" in color.value
    ) {
      return color.value.value;
    }

    // Handle any object with a string value property
    if ("value" in color && typeof color.value === "string") {
      return color.value;
    }
  }

  return "#e5e7eb";
};
